import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicPageCard } from '@/components/public-rsvp/PublicPageCard';
import { PublicRSVPForm, type PublicRSVPFormValues } from '@/components/public-rsvp/PublicRSVPForm';
import { fetchPickupLocations } from '@/lib/api/pickupLocations';
import {
  cancelPublicRSVPByToken,
  fetchPublicRSVPByToken,
  updatePublicRSVPByToken,
} from '@/lib/api/publicRsvps';
import type { PickupLocation, PublicRSVP as PublicRSVPRecord } from '@/types/api';
import { PublicShell } from './PublicRSVP';

export function PublicRSVPManage() {
  const { token = '' } = useParams();
  const [rsvp, setRsvp] = useState<PublicRSVPRecord | null>(null);
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRSVP = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const [response, locations] = await Promise.all([
        fetchPublicRSVPByToken(token),
        fetchPickupLocations(true),
      ]);
      setRsvp(response.rsvp);
      setCanEdit(response.canEdit);
      setPickupLocations(locations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load RSVP');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRSVP();
  }, [loadRSVP]);

  const handleUpdate = async (values: PublicRSVPFormValues) => {
    setMessage(null);
    setError(null);

    try {
      setSubmitting(true);
      const response = await updatePublicRSVPByToken(token, values);
      setRsvp(response.rsvp);
      setMessage('RSVP updated successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel this RSVP?')) return;

    setMessage(null);
    setError(null);

    try {
      setSubmitting(true);
      await cancelPublicRSVPByToken(token);
      setCanEdit(false);
      setRsvp((current) => current ? { ...current, status: 'CANCELLED', optedOutMenuRecipeIds: [] } : current);
      setMessage('RSVP cancelled successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to cancel RSVP');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <Card className="p-8 text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
        </Card>
      </PublicShell>
    );
  }

  if (!rsvp || !rsvp.menu) {
    return (
      <PublicShell>
        <Card className="p-8 text-center">
          <p className="font-semibold">RSVP not found</p>
          <p className="text-sm text-muted-foreground">Please use the lookup form from the original RSVP link.</p>
        </Card>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="space-y-4">
        <PublicPageCard
          menu={rsvp.menu}
          statusBadge={rsvp.status === 'CANCELLED' ? (
            <Badge variant={rsvp.status === 'ACTIVE' ? 'outline' : 'destructive'}>
              {rsvp.status === 'ACTIVE' ? 'Active' : 'Cancelled'}
            </Badge>
          ) : undefined}
          footer={(
            <p className="text-sm text-muted-foreground">
              ITS ID: <span className="font-medium text-foreground">{rsvp.itsId}</span>
            </p>
          )}
        />

        {!canEdit && (
          <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {rsvp.status === 'CANCELLED'
              ? 'This RSVP has been cancelled.'
              : 'This RSVP can no longer be edited because cutoff has passed.'}
          </Card>
        )}

        {message && (
          <Card className="border-green-200 bg-green-50 p-4 text-sm text-green-900">
            {message}
          </Card>
        )}

        {error && (
          <Card className="border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </Card>
        )}

        <Card className="shadow-lg border-stone-200/60 dark:border-stone-700/60">
          <CardContent className="pt-6">
            <PublicRSVPForm
              menu={rsvp.menu}
              pickupLocations={pickupLocations}
              initialValues={{
                name: rsvp.name,
                phone: rsvp.phone,
                pickupLocationId: rsvp.pickupLocationId,
                size: rsvp.size,
                mithaas: rsvp.mithaas,
                optedOutMenuRecipeIds: rsvp.optedOutMenuRecipeIds,
              }}
              disabled={!canEdit}
              submitting={submitting}
              submitLabel="Update RSVP"
              onSubmit={handleUpdate}
            />
          </CardContent>
        </Card>

        {canEdit && (
          <Button
            type="button"
            variant="outline"
            className="w-full border-destructive/40 text-destructive shadow-sm hover:border-destructive hover:bg-destructive/10"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel RSVP
          </Button>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Need the public form again?{' '}
          {rsvp.menu.publicRsvpSlug ? (
            <Link to={`/public-rsvp/${rsvp.menu.publicRsvpSlug}`} className="text-primary underline">
              Open RSVP link
            </Link>
          ) : (
            'Ask the admin for the event link.'
          )}
        </p>
      </div>
    </PublicShell>
  );
}
